import { useCallback, useEffect, useState } from "react";
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import type { Feature, GeoJsonProperties, Geometry } from "geojson";
import type { MapRef } from "react-map-gl";
import { bbox } from "@turf/turf";

type FeatureType = Feature<Geometry, GeoJsonProperties>;

interface UseBoundaryFeaturesOptions {
  draw: MapboxDraw;
  mapRef: React.RefObject<MapRef | null> | React.MutableRefObject<MapRef | undefined>;
  initialGeometry?: GeoJSON.FeatureCollection;
  onGeometryChange: (geometry: GeoJSON.FeatureCollection | undefined) => void;
}

export function useBoundaryFeatures({
  draw,
  mapRef,
  initialGeometry,
  onGeometryChange,
}: UseBoundaryFeaturesOptions) {
  const [features, setFeatures] = useState<GeoJSON.Feature<GeoJSON.Geometry>[]>(
    initialGeometry?.features?.map((f) => f as GeoJSON.Feature<GeoJSON.Geometry>) ?? []
  );
  const [selectedFeatureId, setSelectedFeatureId] = useState<string | null>(null);
  const [currentMode, setCurrentMode] = useState<string>("simple_select");
  const [mapReady, setMapReady] = useState(false);

  // Sync features to parent
  const updateGeometry = useCallback(
    (newFeatures: GeoJSON.Feature<GeoJSON.Geometry>[]) => {
      if (newFeatures.length === 0) {
        onGeometryChange(undefined);
      } else {
        onGeometryChange({
          type: "FeatureCollection",
          features: newFeatures,
        });
      }
    },
    [onGeometryChange]
  );

  const addOrUpdateFeatures = useCallback(
    (incoming: FeatureType | FeatureType[]) => {
      const incomingArray = Array.isArray(incoming) ? incoming : [incoming];
      setFeatures((prev) => {
        const existingIds = prev.map((f) => f.id as string);
        const newFeats = incomingArray.filter(
          (f) => !existingIds.includes(f.id as string)
        );
        const updateIds = incomingArray
          .filter((f) => existingIds.includes(f.id as string))
          .map((f) => f.id as string);

        let result = prev.map((f) => {
          if (updateIds.includes(f.id as string)) {
            const updated = incomingArray.find((u) => u.id === f.id)!;
            return {
              ...f,
              geometry: updated.geometry,
              properties: { ...updated.properties, name: f.properties?.name },
            };
          }
          return f;
        });

        result = [...result, ...newFeats].map((f, i) => {
          if (!f.properties) f.properties = {};
          if (!f.properties.name) f.properties.name = `Boundary ${i + 1}`;
          return f;
        });

        updateGeometry(result);
        return result;
      });
    },
    [updateGeometry]
  );

  const handleDelete = useCallback(
    (id: string) => {
      draw.delete([id]);
      setFeatures((prev) => {
        const next = prev.filter((f) => f.id !== id);
        updateGeometry(next);
        return next;
      });
    },
    [draw, updateGeometry]
  );

  const handleRename = useCallback(
    (id: string, name: string) => {
      setFeatures((prev) => {
        const next = prev.map((f) => {
          if (f.id === id && f.properties) {
            return { ...f, properties: { ...f.properties, name } };
          }
          return f;
        });
        updateGeometry(next);
        return next;
      });
    },
    [updateGeometry]
  );

  const replaceFeatures = useCallback(
    (newFeatures: GeoJSON.Feature<GeoJSON.Geometry>[]) => {
      draw.deleteAll();
      const named = newFeatures.map((f, i) => {
        const clone = { ...f, properties: { ...f.properties } };
        if (!clone.properties!.name) clone.properties!.name = `Boundary ${i + 1}`;
        draw.add(clone);
        return clone;
      });
      setFeatures(named);
      updateGeometry(named);
    },
    [draw, updateGeometry],
  );

  const handleSelect = useCallback(
    (id: string) => {
      draw.changeMode("simple_select", { featureIds: [id] });
      setSelectedFeatureId(id);
    },
    [draw]
  );

  // Poll for map readiness
  useEffect(() => {
    const map = mapRef.current;
    if (map) {
      setMapReady(true);
      return;
    }
    const interval = setInterval(() => {
      if (mapRef.current) {
        setMapReady(true);
        clearInterval(interval);
      }
    }, 100);
    return () => clearInterval(interval);
  }, [mapRef]);

  // Wire draw events once map is ready
  useEffect(() => {
    if (!mapReady) return;
    const map = mapRef.current;
    if (!map) return;

    //@ts-ignore
    if (!map.hasControl(draw)) {
      //@ts-ignore
      map.addControl(draw);
    }

    //@ts-ignore
    map.on("draw.create", (e: { features: Feature[] }) => {
      addOrUpdateFeatures(e.features[0]);
    });
    //@ts-ignore
    map.on("draw.update", (e: { features: Feature[] }) => {
      addOrUpdateFeatures(e.features[0]);
    });
    //@ts-ignore
    map.on("draw.selectionchange", (e: { features: Feature[] }) => {
      const first = e.features[e.features.length - 1];
      setSelectedFeatureId(first ? (first.id as string) : null);
    });
    //@ts-ignore
    map.on("draw.modechange", (e: { mode: string }) => {
      setCurrentMode(e.mode);
    });

    // Load existing features into draw and zoom to fit
    if (initialGeometry?.features && initialGeometry.features.length > 0) {
      initialGeometry.features.forEach((f) => draw.add(f));
      const bounds = bbox(initialGeometry);
      mapRef.current?.fitBounds(
        [[bounds[0], bounds[1]], [bounds[2], bounds[3]]],
        { padding: 40 },
      );
    }
  }, [mapReady, draw, addOrUpdateFeatures, initialGeometry, mapRef]);

  return {
    features,
    selectedFeatureId,
    currentMode,
    setCurrentMode,
    mapReady,
    addOrUpdateFeatures,
    replaceFeatures,
    handleDelete,
    handleRename,
    handleSelect,
  };
}

export const GEOMETRY_SIZE_LIMIT_KB = 450;
export const MAX_FEATURES = 20;

export function calculateByteSize(obj: unknown) {
  return new Blob([JSON.stringify(obj)]).size;
}

export function isGeometryTooBig(features: GeoJSON.Feature[]) {
  return calculateByteSize({ features }) > GEOMETRY_SIZE_LIMIT_KB * 1024;
}

export function isTooManyFeatures(features: GeoJSON.Feature[]) {
  return features.length > MAX_FEATURES;
}
